<script setup lang="ts">
import { cva } from "class-variance-authority";
import {
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogOverlay,
	DialogPortal,
	DialogRoot,
	DialogTitle,
} from "reka-ui";
import { computed, inject, watch } from "vue";
import { i18n } from "@/shared/i18n";
import { OVERLAY_TARGET } from "../composables/overlay-target";
import Icon from "./Icon.vue";

/**
 * Design-system primitive: tokens only, no business knowledge, no network.
 *
 * O diálogo — e a primeira vez que a `reka-ui` entra no `src`.
 *
 * ── POR QUE UMA DEPENDÊNCIA E NÃO MAIS UM `fixed inset-0` ─────────────────
 *
 * Havia seis diálogos escritos à mão e nenhum dos seis fechava com Esc, prendia o
 * Tab, travava a rolagem do corpo, punha o foco dentro ao abrir ou o devolvia ao
 * gatilho ao fechar. Cinco comportamentos, seis vezes, é o tipo de coisa que se
 * escreve uma vez e nunca se escreve de novo. A `reka-ui` já estava declarada e
 * paga em disco sem um único import — este arquivo é onde ela passa a servir.
 *
 * ── POR QUE O CONTEÚDO MORA DENTRO DO OVERLAY ────────────────────────────
 *
 * O painel nunca passa da altura do overlay (`max-h-full`) e quem rola é o CORPO
 * dele: um diálogo alto numa janela baixa mantém o título e o X à vista, e nada
 * fica inalcançável. É também o que mantém o `items-end sm:items-center` que
 * os diálogos à mão já faziam: no telemóvel a folha encosta embaixo, perto do
 * polegar.
 *
 * ── POR QUE O `aria-label` REPETE O TÍTULO ────────────────────────────────
 *
 * A `reka-ui@2.10.1` provê `titleId`/`descriptionId` como string vazia no contexto
 * do `DialogRoot` (o `DrawerRoot`, ao lado, usa `useId`), então o `aria-labelledby`
 * do conteúdo aponta para lugar nenhum. Um idref vazio é ignorado e o nome cai no
 * `aria-label` — que é o que este atributo garante. Quando a versão seguinte
 * consertar o contexto, esta linha some e nada mais muda.
 */
const props = withDefaults(
	defineProps<{
		open: boolean;
		/** Sempre existe: é o nome acessível do diálogo, não um enfeite. */
		title: string;
		description?: string;
		size?: "sm" | "md" | "lg" | "xl";
		/**
		 * The X's accessible name — the dialog's only word of its own. The screen
		 * may pass its own; one that passes nothing gets the panel's "Fechar", never
		 * an English "Close" read aloud on a Portuguese screen (QA 19/10).
		 */
		closeLabel?: string;
	}>(),
	{ description: "", size: "md" },
);

const emit = defineEmits<{ "update:open": [open: boolean] }>();

const panel = cva(
	"my-auto flex max-h-full w-full flex-col gap-3 rounded-2xl border border-border bg-surface-raised p-5 shadow-lg focus:outline-none",
	{
		variants: { size: { sm: "max-w-sm", md: "max-w-md", lg: "max-w-lg", xl: "max-w-3xl" } },
		defaultVariants: { size: "md" },
	},
);

const classes = computed(() => panel({ size: props.size }));
const portalTarget = inject(OVERLAY_TARGET, "body");

/**
 * DEVOLVER O FOCO A QUEM ABRIU. A `reka-ui` só o devolve a um `DialogTrigger`, e
 * quase todo diálogo daqui abre por estado (`:open`) — o foco caía no `body` e quem
 * usa teclado recomeçava do topo da página. Guarda-se quem tinha o foco ao abrir.
 */
let opener: HTMLElement | null = null;
watch(
	() => props.open,
	(open) => {
		if (open && typeof document !== "undefined")
			opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
	},
	{ immediate: true },
);
function returnFocus(event: Event): void {
	if (!opener?.isConnected) return;
	event.preventDefault();
	opener.focus();
	opener = null;
}
</script>

<template>
	<DialogRoot :open="open" @update:open="emit('update:open', $event)">
		<DialogPortal :to="portalTarget">
			<DialogOverlay
				class="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/40 p-4 sm:items-center"
			>
				<DialogContent :class="classes" :aria-label="title" @close-auto-focus="returnFocus">
					<header class="flex shrink-0 items-start justify-between gap-3">
						<!-- O selo do diálogo, quando ele tem um. Opcional porque um selo
						     genérico seria decoração: quem sabe qual traço nomeia este
						     assunto é a tela. `aria-hidden` fica com o `Icon`, que já o põe —
						     o nome do diálogo é o título, e nunca a figura ao lado dele. -->
						<div
							v-if="$slots.icon"
							class="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-accent"
						>
							<slot name="icon" />
						</div>
						<div class="min-w-0 flex-1">
							<DialogTitle class="font-serif text-lg font-medium tracking-tight text-fg">
								{{ title }}
							</DialogTitle>
							<DialogDescription v-if="description" class="mt-0.5 text-xs text-fg-muted">
								{{ description }}
							</DialogDescription>
						</div>
						<!-- O X existe mesmo com um "Cancelar" no rodapé: é o gesto de quem abriu
						     sem querer, e é o único que não obriga a ler os botões antes. -->
						<DialogClose
							class="-mr-1 -mt-1 shrink-0 rounded-full p-1.5 text-fg-subtle hover:bg-bg-muted hover:text-fg"
							:aria-label="closeLabel ?? i18n.global.t('action.close')"
						>
							<Icon name="close" />
						</DialogClose>
					</header>

					<!-- Quem rola é o corpo: num diálogo mais alto que a janela, o título e o X ficam à vista. -->
					<div class="-mx-1 min-h-0 space-y-3 overflow-y-auto px-1">
						<slot />
					</div>

					<footer v-if="$slots.footer" class="flex shrink-0 flex-wrap justify-end gap-2 pt-1">
						<slot name="footer" />
					</footer>
				</DialogContent>
			</DialogOverlay>
		</DialogPortal>
	</DialogRoot>
</template>
